import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Check, X, Calendar, Clock, AlertCircle, Phone } from "lucide-react";
import { toast, Toaster } from "sonner";

/**
 * Public quote acceptance page — opened via magic-link from email.
 * URL: /offerte/:id/:token
 */
const QuotePage = () => {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [done, setDone] = useState(null); // 'accepted' | 'declined' | 'expired'

  useEffect(() => {
    document.title = "Ihre Offerte · NIVARA";
    api
      .get(`/booking-requests/${id}/quote/${token}`)
      .then(({ data }) => {
        setReq(data);
        if (data.status === "expired") setDone("expired");
        if (data.status === "confirmed") setDone("accepted");
        if (data.status === "declined") setDone("declined");
      })
      .catch(() => setReq(null))
      .finally(() => setLoading(false));
  }, [id, token]);

  const accept = async () => {
    setActing(true);
    try {
      const { data } = await api.post(`/booking-requests/${id}/accept/${token}`);
      toast.success("Buchung bestätigt!");
      setDone("accepted");
      setReq(data.request);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) {
        setDone("expired");
        toast.error("Diese Offerte ist abgelaufen.");
      } else {
        toast.error("Annahme fehlgeschlagen");
      }
    } finally {
      setActing(false);
    }
  };

  const decline = async () => {
    if (!window.confirm("Offerte wirklich ablehnen?")) return;
    setActing(true);
    try {
      await api.post(`/booking-requests/${id}/decline/${token}`);
      setDone("declined");
      toast.message("Offerte abgelehnt — Danke für Ihre Rückmeldung.");
    } catch (e) {
      toast.error("Fehler");
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="rs-app min-h-screen bg-white">
        <Header />
        <div className="max-w-xl mx-auto px-6 py-20 text-center text-[var(--rs-text-muted)]">Lade Offerte…</div>
        <Footer />
      </div>
    );
  }

  if (!req) {
    return (
      <div className="rs-app min-h-screen bg-white">
        <Header />
        <div className="max-w-xl mx-auto px-6 py-20 text-center">
          <AlertCircle size={36} className="mx-auto text-red-600" />
          <h1 className="font-[Chivo] text-3xl font-black mt-4">Offerte nicht gefunden</h1>
          <p className="text-[var(--rs-text-muted)] mt-3">
            Der Link ist ungültig oder die Offerte wurde widerrufen.
          </p>
          <Link to="/" className="rs-btn-primary inline-flex mt-6">Zur Startseite</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const q = req.quote || {};

  return (
    <div className="rs-app min-h-screen bg-white" data-testid="quote-page">
      <Header />
      <Toaster position="top-center" />
      <main className="max-w-2xl mx-auto px-6 py-14 sm:py-20">
        <div className="rs-overline">Offerte</div>
        <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black mt-2 leading-tight" data-testid="quote-number">
          {req.request_number}
        </h1>

        {done === "expired" && (
          <div className="mt-6 bg-red-50 border-2 border-red-600 p-5">
            <div className="flex items-center gap-2 text-red-700 font-bold uppercase tracking-wider">
              <AlertCircle size={18} /> Offerte abgelaufen
            </div>
            <p className="text-sm mt-2">Diese Offerte war 48 Stunden gültig. Bitte stellen Sie eine neue Anfrage oder rufen Sie uns an.</p>
            <a href="tel:+41793966660" className="rs-btn-primary inline-flex mt-4">
              <Phone size={16} className="mr-2" /> 079 396 66 60
            </a>
          </div>
        )}

        {done === "accepted" && (
          <div className="mt-6 bg-[var(--rs-primary)] text-white p-6">
            <div className="flex items-center gap-2 font-bold uppercase tracking-wider">
              <Check size={20} /> Buchung bestätigt
            </div>
            <p className="text-sm mt-2 text-white/90">
              Vielen Dank! Sie erhalten eine Bestätigungs-E-Mail mit allen Details.
              Wir sehen uns am Übergabetermin.
            </p>
          </div>
        )}

        {done === "declined" && (
          <div className="mt-6 bg-[var(--rs-surface)] border border-[var(--rs-border)] p-5">
            <div className="font-bold">Offerte abgelehnt</div>
            <p className="text-sm text-[var(--rs-text-muted)] mt-1">Danke für Ihre Rückmeldung. Wir freuen uns auf eine andere Gelegenheit.</p>
          </div>
        )}

        {/* Price hero */}
        <div className="mt-8 bg-black text-white p-8" data-testid="quote-price-block">
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/60">Gesamtpreis</div>
          <div className="font-[Chivo] text-[52px] font-black leading-none mt-2">
            CHF {Number(q.price_chf || 0).toLocaleString("de-CH")}.<span className="text-[28px] align-super">–</span>
          </div>
          <div className="text-sm text-white/80 mt-3">{req.vehicle_name} ({req.vehicle_plate})</div>
        </div>

        {/* Details */}
        <div className="mt-8 space-y-3 border-2 border-[var(--rs-border)] p-5">
          <Row icon={Calendar} label="Übergabe" value={`${req.rental_start || ""} um ${q.pickup_time || ""}`} />
          <Row icon={Calendar} label="Rückgabe" value={`${req.rental_end || "—"} um ${q.return_time || ""}`} />
          <Row label="Reiseziel" value={req.destination === "eu" ? "Europa (EU)" : req.destination === "ch" ? "Schweiz" : "—"} />
          {q.admin_notes && <Row label="Anmerkung" value={q.admin_notes} />}
          <Row
            icon={Clock}
            label="Gültig bis"
            value={req.quote_expires_at ? new Date(req.quote_expires_at).toLocaleString("de-CH") : "—"}
          />
        </div>

        <p className="text-xs text-[var(--rs-text-muted)] mt-6 leading-relaxed">
          Mit Annahme wird die Buchung verbindlich. Zahlung erfolgt per TWINT oder bar bei Fahrzeug-Übergabe.
          Es gelten unsere{" "}
          <Link to="/agb" className="underline text-[var(--rs-primary)]">AGB</Link>.
        </p>

        {!done && (
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={accept}
              disabled={acting}
              className="rs-btn-primary justify-center flex-1 disabled:opacity-50"
              data-testid="quote-accept-btn"
            >
              <Check size={18} className="mr-2" /> Offerte annehmen
            </button>
            <button
              type="button"
              onClick={decline}
              disabled={acting}
              className="rs-btn-secondary justify-center disabled:opacity-50"
              data-testid="quote-decline-btn"
            >
              <X size={16} className="mr-2" /> Ablehnen
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 text-sm">
    {Icon && <Icon size={16} className="text-[var(--rs-primary)] mt-0.5" />}
    <div className="flex-1 flex items-baseline justify-between gap-3">
      <span className="text-[var(--rs-text-muted)] text-xs uppercase tracking-wider">{label}</span>
      <span className="font-bold text-right">{value}</span>
    </div>
  </div>
);

export default QuotePage;
